import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/auth'
import type {
  User,
  AuthTokens,
  AccessTokenResponse,
  LoginCredentials,
  RegisterData,
  OnboardingData,
  OnboardingAnswers,
  RoutePreferences,
} from '@/types/auth'

/**
 * SR-38 — wizard priority label → scoring criterion key.
 *
 * Keys are the BACKEND registry names (`services/scoring/registry.py`). Since
 * SR-39 the app's criteria list is fetched from `GET /scoring/criteria` (see
 * stores/criteria.ts), so this map's keys agree with the rest of the app again.
 * `PreferencesUpdate.selected_criteria` is still validated against nothing
 * backend-side, so a wrong name here would be accepted and stored silently.
 * That is why the test asserts the exact strings.
 *
 * "Flat terrain" is not a priority *for* a criterion so much as a direction on
 * one — it selects the `elevation` criterion and also sets
 * `elevation_preference`, which is a first-class field.
 */
const PRIORITY_TO_CRITERION: Record<string, string> = {
  Greenness: 'greenness',
  'Quiet routes': 'noise',
  Shade: 'shade_cool',
  'Flat terrain': 'elevation',
  'Scenic views': 'scenery',
}

/**
 * Wizard answers → `POST /users/me/onboarding` body.
 *
 * Only `experienceLevel` has an honest destination: it shares a vocabulary with
 * `fitness_level` (beginner / intermediate / advanced).
 *
 * `weeklyDistance` is deliberately NOT mapped. The API offers
 * `running_frequency` (runs per week) and `running_pace_min_km`, and a distance
 * band answers neither — "30-50 km/week" says nothing about how many runs or how
 * fast. Guessing would replace lost data with invented data, which is worse.
 * Tracked in SR-38 as a UI/API mismatch to resolve, not papered over here.
 */
export function toOnboardingData(answers: OnboardingAnswers): OnboardingData {
  const data: OnboardingData = {}
  const level = answers.experienceLevel
  if (level === 'beginner' || level === 'intermediate' || level === 'advanced') {
    data.fitness_level = level
  }
  return data
}

/** Wizard answers → `PUT /users/me/preferences` body (a merge; see SR-38). */
export function toRoutePreferences(answers: OnboardingAnswers): Partial<RoutePreferences> {
  const criteria = answers.priorities
    .map((p) => PRIORITY_TO_CRITERION[p])
    .filter((c): c is string => Boolean(c))

  if (criteria.length === 0) return {}

  const prefs: Partial<RoutePreferences> = {
    selected_criteria: criteria,
    // Selection order is the user's own ranking — preserved rather than sorted.
    priority_ranking: criteria,
  }
  if (answers.priorities.includes('Flat terrain')) {
    prefs.elevation_preference = 'flat'
  }
  return prefs
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const redirectPath = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isPremium = computed(() => user.value?.subscription_tier === 'premium')
  const isDemo = computed(() => user.value?.subscription_tier === 'demo')
  const tier = computed(() => user.value?.subscription_tier || 'anonymous')
  const hasCompletedOnboarding = computed(() => user.value?.onboarding_completed ?? false)

  // Anonymous session tracking (for route generation limits)
  const anonymousSessionId = ref<string | null>(null)
  const anonymousRouteCount = ref(0)

  // Actions
  async function initialize() {
    if (isInitialized.value) return

    isLoading.value = true
    error.value = null

    try {
      // Try to restore session from stored token
      const storedToken = localStorage.getItem('access_token')
      if (storedToken) {
        accessToken.value = storedToken
        // Validate token and get user
        const userData = await authService.getCurrentUser()
        user.value = userData
      } else {
        // Initialize anonymous session
        initAnonymousSession()
      }
    } catch (err) {
      // Token invalid or expired, try refresh
      try {
        await refreshToken()
      } catch {
        // Refresh failed, clear auth state
        clearAuth()
        initAnonymousSession()
      }
    } finally {
      isInitialized.value = true
      isLoading.value = false
    }
  }

  function initAnonymousSession() {
    // Get or create anonymous session ID
    let sessionId = localStorage.getItem('anonymous_session_id')
    if (!sessionId) {
      sessionId = crypto.randomUUID()
      localStorage.setItem('anonymous_session_id', sessionId)
    }
    anonymousSessionId.value = sessionId

    // Get route count for this session
    const count = parseInt(localStorage.getItem('anonymous_route_count') || '0', 10)
    anonymousRouteCount.value = count
  }

  function incrementAnonymousRouteCount() {
    anonymousRouteCount.value++
    localStorage.setItem('anonymous_route_count', anonymousRouteCount.value.toString())
  }

  async function login(credentials: LoginCredentials) {
    isLoading.value = true
    error.value = null

    try {
      const tokens = await authService.login(credentials)
      setTokens(tokens)

      // Get user data
      const userData = await authService.getCurrentUser()
      user.value = userData

      // Clear anonymous session
      clearAnonymousSession()

      return userData
    } catch (err: any) {
      error.value = err.message || 'Login failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function register(data: RegisterData) {
    isLoading.value = true
    error.value = null

    try {
      const result = await authService.register(data)
      return result
    } catch (err: any) {
      error.value = err.message || 'Registration failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    isLoading.value = true

    try {
      await authService.logout()
    } catch {
      // Ignore logout errors
    } finally {
      clearAuth()
      initAnonymousSession()
      isLoading.value = false
    }
  }

  async function refreshToken() {
    try {
      const tokens = await authService.refreshToken()
      setTokens(tokens)

      // Update user data
      const userData = await authService.getCurrentUser()
      user.value = userData
    } catch (err) {
      clearAuth()
      throw err
    }
  }

  // Accepts both wire shapes (login-shaped AuthTokens, refresh-shaped
  // AccessTokenResponse) — only access_token is consumed either way.
  function setTokens(tokens: AuthTokens | AccessTokenResponse) {
    accessToken.value = tokens.access_token
    localStorage.setItem('access_token', tokens.access_token)
  }

  function clearAuth() {
    user.value = null
    accessToken.value = null
    localStorage.removeItem('access_token')
  }

  function clearAnonymousSession() {
    anonymousSessionId.value = null
    anonymousRouteCount.value = 0
    localStorage.removeItem('anonymous_session_id')
    localStorage.removeItem('anonymous_route_count')
  }

  function setRedirectPath(path: string | null) {
    redirectPath.value = path
  }

  function getAndClearRedirectPath(): string | null {
    const path = redirectPath.value
    redirectPath.value = null
    return path
  }

  async function updateProfile(profileData: Partial<User>) {
    isLoading.value = true
    error.value = null

    try {
      const updatedUser = await authService.updateProfile(profileData)
      user.value = { ...user.value, ...updatedUser } as User
      return updatedUser
    } catch (err: any) {
      error.value = err.message || 'Profile update failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function completeOnboarding(onboardingData: OnboardingData) {
    isLoading.value = true
    error.value = null

    try {
      const updatedUser = await authService.completeOnboarding(onboardingData)
      user.value = { ...user.value, ...updatedUser, onboarding_completed: true } as User
      return updatedUser
    } catch (err: any) {
      error.value = err.message || 'Onboarding failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * SR-38 — submit the onboarding wizard's answers for real.
   *
   * `Onboarding.vue` used to call `completeOnboarding({})`. The store method is
   * not a stub — it really posts and really marks onboarding complete — so the
   * wizard walked the user through three steps, sent none of their answers, and
   * reported success. Because the completion flag *was* written, the user was
   * never asked again. Not a false claim like its sibling stubs: silent,
   * irreversible data loss wearing a success state.
   *
   * The mapping lives here rather than in the component for the reason the bug
   * itself demonstrates — a payload built inside a component with no DOM test
   * environment is a payload nobody can assert on. The component now hands over
   * the whole answers object and holds no field logic, so it cannot drop a field
   * again without this function changing.
   */
  async function submitOnboardingAnswers(answers: OnboardingAnswers) {
    const profile = toOnboardingData(answers)
    const prefs = toRoutePreferences(answers)

    // Profile first: it carries the completion flag, and a preferences failure
    // must not leave the user stuck re-answering the wizard forever.
    const updated = await completeOnboarding(profile)

    if (Object.keys(prefs).length > 0) {
      await authService.updatePreferences(prefs)
    }

    return updated
  }

  return {
    // State
    user,
    accessToken,
    isInitialized,
    isLoading,
    error,
    redirectPath,
    anonymousSessionId,
    anonymousRouteCount,

    // Getters
    isAuthenticated,
    isPremium,
    isDemo,
    tier,
    hasCompletedOnboarding,

    // Actions
    initialize,
    login,
    register,
    logout,
    refreshToken,
    setRedirectPath,
    getAndClearRedirectPath,
    updateProfile,
    completeOnboarding,
    submitOnboardingAnswers,
    incrementAnonymousRouteCount,
  }
})
